<template><div><dt-notice kind="warning" title="Use DtText over CSS Utilities" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Reach for the <a href="/components/text" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtText</a> component before considering any typography utility.
</dt-notice>
<p class="d-docsite--paragraph">The <code v-pre>font-variant-numeric</code> CSS property controls the usage of alternate glyphs for numbers, fractions, and ordinal markers.</p>
<h2 id="normal-default" tabindex="-1" class="d-docsite--header-2">Normal (Default) <a class="header-anchor d-link d-docsite--link d-link" href="#normal-default" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-normal</code> to deactivate any alternate glyphs.</p>
<code-example source-code='&lt;p class=&quot;d-fvn-normal&quot;&gt;1234567890&lt;/p&gt;'>
<p class="d-fvn-normal">1234567890</p>
</code-example>
<h2 id="ordinal" tabindex="-1" class="d-docsite--header-2">Ordinal <a class="header-anchor d-link d-docsite--link d-link" href="#ordinal" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The class <code v-pre>d-fvn-ordinal</code> forces the use of special glyphs for ordinal markers, like 1st, 2nd, 3rd.</p>
<code-example source-code='&lt;p class=&quot;d-fvn-ordinal&quot;&gt;1st, 2nd, 3rd, 4th&lt;/p&gt;'>
<p class="d-fvn-ordinal">1st, 2nd, 3rd, 4th</p>
</code-example>
<h2 id="proportional-nums" tabindex="-1" class="d-docsite--header-2">Proportional-Nums <a class="header-anchor d-link d-docsite--link d-link" href="#proportional-nums" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-proportional</code> to set different sizes for each number.</p>
<code-example source-code='&lt;div class=&quot;d-fvn-proportional&quot;&gt;
  &lt;table class=&quot;numbers-table&quot;&gt;
    &lt;tbody&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;21.0&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;160&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.90&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;2.875&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;17.02&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;22.8&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;108&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.85&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;2.320&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;18.61&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;21.4&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;258&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.08&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.215&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;19.44&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
  &lt;/tbody&gt;&lt;/table&gt;
&lt;/div&gt;'>
<div class="d-fvn-proportional">
  <table class="numbers-table">
    <tbody>
    <tr>
      <td><span>21.0</span></td>
      <td><span>160</span></td>
      <td><span>3.90</span></td>
      <td><span>2.875</span></td>
      <td><span>17.02</span></td>
    </tr>
    <tr>
      <td><span>22.8</span></td>
      <td><span>108</span></td>
      <td><span>3.85</span></td>
      <td><span>2.320</span></td>
      <td><span>18.61</span></td>
    </tr>
    <tr>
      <td><span>21.4</span></td>
      <td><span>258</span></td>
      <td><span>3.08</span></td>
      <td><span>3.215</span></td>
      <td><span>19.44</span></td>
    </tr>
  </tbody></table>
</div>
</code-example>
<h2 id="tabular-nums" tabindex="-1" class="d-docsite--header-2">Tabular-Nums <a class="header-anchor d-link d-docsite--link d-link" href="#tabular-nums" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-tabular</code> for aligning tabular data and preventing layout shifts of dynamically-changing content. For example, a timer counting down.</p>
<code-example source-code='&lt;div class=&quot;d-fvn-tabular&quot;&gt;
  &lt;table class=&quot;numbers-table&quot;&gt;
    &lt;tbody&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;21.0&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;160&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.90&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;2.875&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;17.02&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;22.8&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;108&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.85&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;2.320&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;18.61&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
    &lt;tr&gt;
      &lt;td&gt;&lt;span&gt;21.4&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;258&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.08&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;3.215&lt;/span&gt;&lt;/td&gt;
      &lt;td&gt;&lt;span&gt;19.44&lt;/span&gt;&lt;/td&gt;
    &lt;/tr&gt;
  &lt;/tbody&gt;&lt;/table&gt;
&lt;/div&gt;'>
<div class="d-fvn-tabular">
  <table class="numbers-table">
    <tbody>
    <tr>
      <td><span>21.0</span></td>
      <td><span>160</span></td>
      <td><span>3.90</span></td>
      <td><span>2.875</span></td>
      <td><span>17.02</span></td>
    </tr>
    <tr>
      <td><span>22.8</span></td>
      <td><span>108</span></td>
      <td><span>3.85</span></td>
      <td><span>2.320</span></td>
      <td><span>18.61</span></td>
    </tr>
    <tr>
      <td><span>21.4</span></td>
      <td><span>258</span></td>
      <td><span>3.08</span></td>
      <td><span>3.215</span></td>
      <td><span>19.44</span></td>
    </tr>
  </tbody></table>
</div>
</code-example>
<h2 id="diagonal-fractions" tabindex="-1" class="d-docsite--header-2">Diagonal-Fractions <a class="header-anchor d-link d-docsite--link d-link" href="#diagonal-fractions" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-diagonal</code> to display numerator and denominator smaller and separated by a slash.</p>
<code-example source-code='&lt;p class=&quot;d-fvn-diagonal&quot;&gt;1/2 3/4 5/6&lt;/p&gt;'>
<p class="d-fvn-diagonal">1/2 3/4 5/6</p>
</code-example>
<h2 id="unset" tabindex="-1" class="d-docsite--header-2">Unset <a class="header-anchor d-link d-docsite--link d-link" href="#unset" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-unset</code> to deactivate other value previously set to <code v-pre>font-variant-numeric</code>.</p>
<code-example source-code='&lt;p class=&quot;d-fvn-unset&quot;&gt;1234567890&lt;/p&gt;'>
<p class="d-fvn-unset">1234567890</p>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<utility-class-table>
  <template #content>
    <tbody>
      <tr v-for="item in fontVariantNumeric">
        <th scope="row" class="d-code--sm d-docsite-code">.d-fvn-{{ item.class }}</th>
        <td class="d-code--sm">font-variant-numeric: {{ item.output }} !important;</td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
</div></template>


<script setup>
  import { fontVariantNumeric } from '@data/type.json';
</script>
<style lang="less" scoped>
  .numbers-table {
    border-collapse: collapse;
  }
  .numbers-table td {
    border: var(--dt-size-border-100) solid var(--dt-color-border-default);
  }
</style>